import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { usePricing } from "../../../product/hooks";
import { useUpsertQuote } from "../../../order/hooks/quotes";
import { moneyFormatter } from "../../../core/utils/formatters";
import { Visible } from "../../../core/components";
import { FieldGroup, Loading } from "../../../components";
import { isMulticurrency } from "../../../core/currency";
import FormikAutoSave from "./FormikAutoSave";
import { Badge, Button } from "../../../components/ui";

const afterFee = (fee, id, items) => {
  const price = items.find((item) => item.id === id)?.price;

  return (price - price * (fee / 100)) / 100;
};

const preparePricing = async (pricing, setPricingItems) => {
  if (!Array.isArray(pricing)) {
    return [];
  }

  const pricingOptions = pricing
    .filter((item) => ["uk", "europe"].includes(item.location))
    .map((item) => ({
      id: item.id,
      name: [
        moneyFormatter(item.gbp_price / 100, "gbp"),
        item.supplier_name
          ? item.supplier_name + " | " + item.location?.toUpperCase()
          : item.location?.toUpperCase(),
      ]
        .filter((val) => val)
        .join(" | "),
      location: item.location,
      condition: item.condition,
      price: item.gbp_price,
    }));

  setPricingItems(pricingOptions);
};

const validationSchema = Yup.object().shape({
  worn_fee: Yup.number().typeError("Invalid"),
  worn_uk_price_id: Yup.string(),
  worn_eu_price_id: Yup.string(),
  new_fee: Yup.number().typeError("Invalid"),
  new_uk_price_id: Yup.string(),
  new_eu_price_id: Yup.string(),
});

const priceFields = [
  "new_uk_price",
  "new_eu_price",
  "worn_uk_price",
  "worn_eu_price",
];

// The quote workspace's CH24 market data: a Worn card and a New card, each
// Fee % · Cheapest UK · After fee / Cheapest EU · After fee, labelled above.
const MarketSide = ({ side, title, fee, ukId, euId, options, gbpTag }) => {
  const isNew = side === "new";
  const pick = (location) =>
    options.filter((o) => o.location === location && (o.condition === '["new"]') === isNew);
  return (
    <section className="qws-card" aria-label={title}>
      <h4 className="qws-card__title">{title}</h4>
      <div className="qws-form-grid qws-form-grid--3">
        <div className="qws-field">
          <FieldGroup name={`${side}_fee`} type="numberFormat" label="Fee %" placeholder="0" />
        </div>
        <div className="qws-field">
          <FieldGroup name={`${side}_uk_price_id`} type="select" options={pick("uk")} label="Cheapest UK" placeholder="Select…" />
        </div>
        <div className="qws-field">
          <FieldGroup name={`${side}_uk_price`} type="numberFormat" label={`After fee${gbpTag}`} value={afterFee(fee, ukId, options)} readOnly />
        </div>
        <div className="qws-field qws-field--spacer" aria-hidden="true" />
        <div className="qws-field">
          <FieldGroup name={`${side}_eu_price_id`} type="select" options={pick("europe")} label="Cheapest EU" placeholder="Select…" />
        </div>
        <div className="qws-field">
          <FieldGroup name={`${side}_eu_price`} type="numberFormat" label={`After fee${gbpTag}`} value={afterFee(fee, euId, options)} readOnly />
        </div>
      </div>
    </section>
  );
};

// autoSave: the quote workspace (new setup) — saves on change, no Save button.
// layout="grid": the workspace's two cards; anything else is the modal's.
export function MarketDataForm({ id, quote, item, autoSave = false, layout = "legacy" }) {
  const { upsertQuote } = useUpsertQuote();
  const { pricing, isLoading: isLoadingPricing } = usePricing(
    item?.product_id,
    "chrono24"
  );
  const [error, setError] = useState(null);
  const [pricingItems, setPricingItems] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);

  // The Chrono24 cheapest-price options render with an explicit £ (moneyFormatter
  // 'gbp'); the derived "AFTER FEE" numbers are bare GBP. Tag them GBP when
  // multi-currency is on so they aren't read as the quote currency. Empty
  // (byte-identical) while off; read at render time (seeding runs post-import).
  const gbpTag = isMulticurrency() ? " (GBP)" : "";

  useEffect(() => {
    if (pricing?.length) {
      preparePricing(pricing, setPricingItems);
    }
  }, [pricing?.length]);

  const initialValues = useMemo(() => quote.market_data || {}, [
    quote.market_data,
  ]);

  if (isLoadingPricing) {
    return <Loading />;
  }

  const handleSubmit = async (data) => {
    setError(null);

    const marketData = Object.keys(data).reduce(
      (md, key) => ({
        ...md,
        [key]: priceFields.includes(key)
          ? Math.round(parseFloat(data[key]) * 100)
          : parseFloat(data[key]),
      }),
      {}
    );

    try {
      await upsertQuote({
        id,
        quoteData: {
          market_data: marketData,
          product_id: quote.product_id,
          user_id: quote.user_id,
          currency: quote.currency,
          price: quote.price,
          date: quote.date,
        },
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      setError(
        error?.response?.data?.errors?.[0]?.[0] ||
          "The quote market data could not be saved"
      );
    }
  };

  return (
    <div className="quote-form">
      <Formik
        {...{
          initialValues,
          validationSchema,
          onSubmit: handleSubmit,
        }}
      >
        {({
          values: {
            worn_fee,
            worn_uk_price_id,
            worn_eu_price_id,
            new_fee,
            new_uk_price_id,
            new_eu_price_id,
          },
        }) => layout === "grid" ? (
          <Form className="qws-cards">
            <MarketSide side="worn" title="Worn" fee={worn_fee} ukId={worn_uk_price_id} euId={worn_eu_price_id} options={pricingItems} gbpTag={gbpTag} />
            <MarketSide side="new" title="New" fee={new_fee} ukId={new_uk_price_id} euId={new_eu_price_id} options={pricingItems} gbpTag={gbpTag} />
            <Visible when={error}>
              <div className="error">{error}</div>
            </Visible>
            {autoSave && <FormikAutoSave />}
          </Form>
        ) : (
          <Form>
            <div className="col-6 ">
              <div className="row">
                <div className="col-12 col-sm-6">
                  <div className="form-subsection-title">Worn:</div>
                </div>

                <div className="col-12 col-sm-6">
                  <FieldGroup
                    name="worn_fee"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{
                      paddingLeft: "80px",
                      border: !worn_fee ? "2px solid #FF8500" : undefined,
                    }}
                    placeholder="Fee %:"
                    className={!worn_fee ? "empty-field" : ""}
                  />
                </div>

                <div
                  className={`col-12 col-sm-6 persistent-label-worn ${
                    !worn_uk_price_id ? "empty-field" : ""
                  }`}
                >
                  <FieldGroup
                    name="worn_uk_price_id"
                    type="select"
                    options={pricingItems.filter(
                      (item) =>
                        item.location === "uk" && item.condition !== '["new"]'
                    )}
                    persistLabel
                    placeholder="Cheapest UK (worn):"
                  />
                </div>

                <div className="col-12 col-sm-6 readonly">
                  <FieldGroup
                    name="worn_uk_price"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{ paddingLeft: "110px" }}
                    placeholder={`After fee${gbpTag}:`}
                    value={afterFee(worn_fee, worn_uk_price_id, pricingItems)}
                    readOnly
                  />
                </div>

                <div
                  className={`col-12 col-sm-6 persistent-label-worn ${
                    !worn_eu_price_id ? "empty-field" : ""
                  }`}
                >
                  <FieldGroup
                    name="worn_eu_price_id"
                    type="select"
                    options={pricingItems.filter(
                      (item) =>
                        item.location === "europe" &&
                        item.condition !== '["new"]'
                    )}
                    persistLabel
                    placeholder="Cheapest EU (worn):"
                  />
                </div>

                <div className="col-12 col-sm-6 readonly">
                  <FieldGroup
                    name="worn_eu_price"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{ paddingLeft: "110px" }}
                    placeholder={`After fee${gbpTag}:`}
                    value={afterFee(worn_fee, worn_eu_price_id, pricingItems)}
                    readOnly
                  />
                </div>

                <div className="col-12 col-sm-6">
                  <div className="form-subsection-title">New:</div>
                </div>

                <div className="col-12 col-sm-6">
                  <FieldGroup
                    name="new_fee"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{
                      paddingLeft: "80px",
                      border: !new_fee ? "2px solid #FF8500" : undefined,
                    }}
                    placeholder="Fee %:"
                    className={!new_fee ? "empty-field" : ""}
                  />
                </div>

                <div
                  className={`col-12 col-sm-6 persistent-label-new ${
                    !new_uk_price_id ? "empty-field" : ""
                  }`}
                >
                  <FieldGroup
                    name="new_uk_price_id"
                    type="select"
                    options={pricingItems.filter(
                      (item) =>
                        item.location === "uk" && item.condition === '["new"]'
                    )}
                    persistLabel
                    placeholder="Cheapest UK (new):"
                  />
                </div>

                <div className="col-12 col-sm-6 readonly">
                  <FieldGroup
                    name="new_uk_price"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{ paddingLeft: "110px" }}
                    placeholder={`After fee${gbpTag}:`}
                    value={afterFee(new_fee, new_uk_price_id, pricingItems)}
                    readOnly
                  />
                </div>

                <div
                  className={`col-12 col-sm-6 persistent-label-new ${
                    !new_eu_price_id ? "empty-field" : ""
                  }`}
                >
                  <FieldGroup
                    name="new_eu_price_id"
                    type="select"
                    options={pricingItems.filter(
                      (item) =>
                        item.location === "europe" &&
                        item.condition === '["new"]'
                    )}
                    persistLabel
                    placeholder="Cheapest EU (new):"
                  />
                </div>

                <div className="col-12 col-sm-6 readonly">
                  <FieldGroup
                    name="new_eu_price"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{ paddingLeft: "110px" }}
                    placeholder={`After fee${gbpTag}:`}
                    value={afterFee(new_fee, new_eu_price_id, pricingItems)}
                    readOnly
                  />
                </div>
              </div>

              <Visible when={error}>
                <div className="row">
                  <div className="col-12 col-sm-6 p-1"></div>
                  <div className="col-12 col-sm-6 p-1">
                    <div className="error">{error}</div>
                  </div>
                </div>
              </Visible>

              {autoSave ? (
                <FormikAutoSave />
              ) : (
              <div className="row">
                <div className="col-12 col-sm-6 p-1">
                  {showSuccess && (
                    <Badge tone="success">Saved</Badge>
                  )}
                </div>
                <div className="col-12 col-sm-6 p-1">
                  <Button variant="primary" size="lg" fullWidth type="submit" data-btn="quote-market-save">
                    Save
                  </Button>
                </div>
              </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
